/**
 * OwnerSelect.tsx — the owner picklist. Options are the tenant's CURRENT
 * employees (leavers excluded) from /v1/hr/employees.
 *
 * Since E360-2 the value is the employee's ID, not their name: the server
 * stores assigned_employee_id (composite FK to employees) and REFUSES a
 * name, so what this control emits is now the only accepted owner wire.
 * Top performers groups by that id, which is what finally stops a rename or
 * a spelling variant from splitting one person into two rows. If the
 * employees read fails the select degrades to "Unassigned" only — never a
 * free-text fallback.
 */
import { useAsync, fetchDirectory } from '../../api'

export function OwnerSelect({ value, onChange, className, inheritHint }: {
  /** The selected employee's ID ('' = unassigned). */
  value: string
  onChange: (employeeId: string) => void
  className: string
  /** Convert-modal variant: label the empty option with the inherited
   *  owner instead of "Unassigned" (module-side fallback, §8.D). */
  inheritHint?: string
}) {
  const employees = useAsync(() => fetchDirectory(), [])
  const people = (employees.data ?? []).filter(e => e.status !== 'leaver')
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      className={className} disabled={employees.loading}>
      <option value="">
        {employees.loading ? 'Loading people…'
          : employees.error ? 'People unavailable — leave unassigned'
          : inheritHint || 'Unassigned'}
      </option>
      {people.map(e => (
        <option key={e.id} value={e.id}>{e.fullName}</option>
      ))}
    </select>
  )
}
